/**
 * Configuration for clickable MOPs in the WellStatusGrid
 *
 * To make a MOP clickable:
 * 1. Add a new entry to CLICKABLE_MOPS_CONFIG
 * 2. Specify exactMatch array for exact label matches
 * 3. Specify contains array for partial label matches
 *
 * Example:
 * NEW_MOP: {
 *   exactMatch: ['EXACT_LABEL_1', 'EXACT_LABEL_2'],
 *   contains: ['PARTIAL_KEYWORD'],
 * }
 */

export interface ClickableMopConfig {
  exactMatch: string[];
  contains: string[];
}

export interface ClickableMopsConfiguration {
  [key: string]: ClickableMopConfig;
}

export const CLICKABLE_MOPS_CONFIG: ClickableMopsConfiguration = {
  ESP: {
    exactMatch: ['ESP', 'ELECTRICAL SUBMERSIBLE PUMP'],
    contains: [],
  },
  GAS_LIFT: {
    exactMatch: ['GAS'],
    contains: [],
  },
  //Uncomment and configure additional MOPs as needed:
  ROD_LIFT: {
    exactMatch: ['ROD', 'ROD LIFT', 'ROD-LIFT', 'RODLIFT', 'SRP'],
    contains: ['ROD'],
  },
  // PCP: {
  //   exactMatch: ['PCP'],
  //   contains: [],
  // },
  // CHEMICAL_INJECTION: {
  //   exactMatch: ['CHEMICAL INJECTION', 'CHEMICAL-INJECTION', 'CHEMICALINJECTION'],
  //   contains: ['CHEMICAL'],
  // },
  // FACILITY: {
  //   exactMatch: ['FACILITY'],
  //   contains: [],
  // },
};

/**
 * Helper function to check if a MOP is clickable based on configuration
 * @param mopLabel - The MOP label to check
 * @returns boolean indicating if the MOP should be clickable
 */
export const isMopClickable = (mopLabel: string): boolean => {
  const upperLabel = mopLabel.toUpperCase().trim();

  return Object.values(CLICKABLE_MOPS_CONFIG).some((config) => {
    // Check exact matches
    const hasExactMatch = config.exactMatch.some((exact) => upperLabel === exact.toUpperCase().trim());

    // Check contains matches
    const hasContainsMatch = config.contains.some((keyword) => upperLabel.includes(keyword.toUpperCase().trim()));

    return hasExactMatch || hasContainsMatch;
  });
};

/**
 * Get all currently configured clickable MOP types
 * @returns Array of MOP type names that are configured as clickable
 */
export const getClickableMopTypes = (): string[] => {
  return Object.keys(CLICKABLE_MOPS_CONFIG);
};

/**
 * Add a new clickable MOP configuration
 * @param mopType - The type identifier for the MOP
 * @param config - The configuration for exact and partial matches
 */
export const addClickableMop = (mopType: string, config: ClickableMopConfig): void => {
  CLICKABLE_MOPS_CONFIG[mopType] = config;
};

/**
 * Remove a clickable MOP configuration
 * @param mopType - The type identifier for the MOP to remove
 */
export const removeClickableMop = (mopType: string): void => {
  delete CLICKABLE_MOPS_CONFIG[mopType];
};
